/**
 * src/components/providers/AuthProvider.tsx
 * =============================================================================
 * Proveedor de sesión global (post-migración a Better Auth, 2026-05-26).
 *
 * Responsabilidades:
 *  1. Al montar, llama a `getSession()` para saber si hay cookie válida.
 *  2. Expone `useAuth()` con { user, session, loading, refresh, signOut }.
 *  3. `refresh()` re-fetcha la sesión — útil tras signup verificado por
 *     email o tras login programático.
 *  4. `signOut()` invalida server-side y resetea el estado local.
 *
 * No hay canal en vivo de sesión — Better Auth
 * no expone uno. Si el usuario hace login en otra pestaña, esta pestaña no
 * se entera al instante. Patrón aceptado: a coste de un fetch extra
 * podríamos pollear cada minuto, pero por ahora la UI fuerza refresh tras
 * acciones de auth (login, logout) y eso basta. Si más adelante interesa
 * sync entre pestañas, usamos BroadcastChannel API.
 *
 * ⚠️ REGLA: TODO login programático DEBE llamar a `refresh()`
 * ----------------------------------------------------------
 * Este provider se monta FUERA de `RouterProvider` (ver App.tsx) para que
 * los guards de ruta puedan leerlo. Consecuencia: **solo consulta la sesión
 * al montar**, y un `navigate()` de React Router no lo remonta.
 *
 * Por tanto, cualquier código que llame a `signIn()` de `authService` tiene
 * que llamar después a `refresh()` de este contexto. Si no, la cookie se
 * crea en el servidor pero la app sigue pintando el estado de "no hay
 * sesión" hasta que el usuario recargue a mano.
 *
 * Esto ocurrió de verdad: `LoginPage` no lo hacía y el login quedó roto
 * para cualquier usuario nuevo. No se detectó durante meses porque quien ya
 * tenía cookie de antes nunca lo sufría — solo se veía en un login desde
 * cero. Corregido el 2026-08-14.
 *
 * (El flujo de verificación por email NO necesita esto: Better Auth hace un
 * 302 real al frontend, y eso sí remonta el provider.)
 * =============================================================================
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  getSession,
  signOut as apiSignOut,
  type AuthSession,
  type AuthUser,
} from '@/services/authService'

interface AuthContextValue {
  user: AuthUser | null
  session: AuthSession | null
  loading: boolean
  /** Recarga la sesión desde el servidor (post-login, post-verify, etc.). */
  refresh: () => Promise<void>
  /** Cierra sesión: invalida server-side + resetea estado local. */
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<AuthSession | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const data = await getSession()
      setUser(data?.user ?? null)
      setSession(data?.session ?? null)
    } catch {
      // network error → tratar como no-sesión, el caller decide reintentar
      setUser(null)
      setSession(null)
    }
  }, [])

  const handleSignOut = useCallback(async () => {
    try { await apiSignOut() } catch { /* da igual, limpiamos local */ }
    setUser(null)
    setSession(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      await refresh()
      if (!cancelled) setLoading(false)
    })()
    return () => { cancelled = true }
  }, [refresh])

  return (
    <AuthContext.Provider value={{ user, session, loading, refresh, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook para consumir la sesión en cualquier componente bajo AuthProvider.
 *
 * Disable de `react-refresh/only-export-components`: este archivo exporta
 * el Provider Y el hook (patrón estándar de React docs). Aceptamos el
 * full-reload puntual de HMR a cambio de mantener todo junto.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  }
  return ctx
}
